// src/app/app.routes.ts
import type { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard } from './core/auth/auth.guard';
import { FeatureFlagService } from './core/feature-flags/feature-flag.service';
import type { RuntimeConfig } from './core/config/runtime-config.model';

/**
 * Guard de feature flag — función inline para rutas opcionales.
 * Retorna false si el feature está deshabilitado → redirige al dashboard.
 */
const featureGuard = (flag: keyof RuntimeConfig['features']) =>
  (): boolean => inject(FeatureFlagService).isEnabled(flag);

export const routes: Routes = [
  // Redirigir raíz al dashboard (AuthGuard maneja la autenticación)
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },

  // ── Ruta pública — Login ──────────────────────────────────────────────────
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then(m => m.LoginComponent),
    title: 'Iniciar sesión',
  },

  // ── Rutas protegidas — envueltas por el Layout ────────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('./layout/layout.component').then(m => m.LayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
        title: 'Inicio',
      },
      {
        path: 'loans',
        loadComponent: () =>
          import('./features/loans/loans.component').then(m => m.LoansComponent),
        title: 'Mis Créditos',
      },
      {
        path: 'loans/:loanId',
        loadComponent: () =>
          import('./features/loans/loan-detail/loan-detail.component')
            .then(m => m.LoanDetailComponent),
        title: 'Detalle del crédito',
      },
      {
        path: 'savings',
        canActivate: [featureGuard('savingsModule')],
        loadComponent: () =>
          import('./features/savings/savings.component').then(m => m.SavingsComponent),
        title: 'Mis Ahorros',
      },
      {
        path: 'savings/:accountId',
        canActivate: [featureGuard('savingsModule')],
        loadComponent: () =>
          import('./features/savings/savings-detail/savings-detail.component')
            .then(m => m.SavingsDetailComponent),
        title: 'Detalle de ahorro',
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(m => m.ProfileComponent),
        title: 'Mi Perfil',
      },
      {
        path: 'profile/change-password',
        canActivate: [featureGuard('changePassword')],
        loadComponent: () =>
          import('./features/profile/change-password/change-password.component')
            .then(m => m.ChangePasswordComponent),
        title: 'Cambiar contraseña',
      },
    ],
  },

  // ── Ruta comodín ──────────────────────────────────────────────────────────
  // AuthGuard en el layout padre maneja la autenticación
  { path: '**', redirectTo: '/dashboard' },
];
